import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

/**
 * Editable list of social-media links for a creator.
 * Used inside CreateClan wizard (step 2) and ProfilePage (edit mode).
 *
 * Props:
 *   value      — dict of { platform: { url, verified, followers, verified_at } }
 *   onChange   — called with the merged dict after any save
 *   onError    — called with a human-readable string on validation/API failure
 *   onDirty    — optional, called with true whenever local state differs from server
 */
const PLATFORMS = [
  { key: 'x',         label: 'X (Twitter)',  placeholder: 'https://x.com/yourhandle',           verifiable: true },
  { key: 'youtube',   label: 'YouTube',      placeholder: 'https://youtube.com/@yourchannel',   verifiable: 'soon' },
  { key: 'discord',   label: 'Discord',      placeholder: 'https://discord.gg/yourinvite',      verifiable: 'soon' },
  { key: 'website',   label: 'Website',      placeholder: 'https://yourdomain.com',             verifiable: true },
  { key: 'instagram', label: 'Instagram',    placeholder: 'https://instagram.com/yourhandle',   verifiable: false },
  { key: 'tiktok',    label: 'TikTok',       placeholder: 'https://tiktok.com/@yourhandle',     verifiable: false },
  { key: 'linkedin',  label: 'LinkedIn',     placeholder: 'https://linkedin.com/in/yourhandle', verifiable: false },
];

export default function SocialsEditor({ value, onChange, onError, onDirty }) {
  const [local, setLocal] = useState(() => normalize(value));
  const [saving, setSaving] = useState(false);
  const [busyPlatform, setBusyPlatform] = useState(null);
  const [verifyMsg, setVerifyMsg] = useState({});
  const [userId, setUserId] = useState('');

  useEffect(() => { setLocal(normalize(value)); }, [value]);
  useEffect(() => {
    // Cache user_id for the meta-tag copy hint (website verification).
    api.get('/auth/me').then(({ data }) => setUserId(data?.user?.id || '')).catch(() => {});
  }, []);

  const dirty = useMemo(() => {
    const initial = normalize(value);
    return PLATFORMS.some((p) => (local[p.key]?.url || '') !== (initial[p.key]?.url || ''));
  }, [local, value]);

  useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty, onDirty]);

  const setUrl = (key, url) => {
    setLocal((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), url } }));
    setVerifyMsg((m) => ({ ...m, [key]: null }));
  };

  const save = async () => {
    setSaving(true);
    onError && onError('');
    try {
      const payload = {};
      PLATFORMS.forEach((p) => {
        const url = (local[p.key]?.url || '').trim();
        payload[p.key] = url;
      });
      const { data } = await api.put('/socials', payload);
      onChange && onChange(data.socials || {});
      setLocal(normalize(data.socials || {}));
      return true;
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      onError && onError(typeof detail === 'string' ? detail : 'Could not save. Try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const verifyWebsite = async () => {
    if (dirty) {
      const ok = await save();
      if (!ok) return;
    }
    setBusyPlatform('website');
    setVerifyMsg((m) => ({ ...m, website: null }));
    try {
      const { data } = await api.post('/socials/website/verify');
      onChange && onChange(data.socials || {});
      setLocal(normalize(data.socials || {}));
      setVerifyMsg((m) => ({ ...m, website: { ok: true, text: 'Verified ✓' } }));
    } catch (ex) {
      const detail = ex.response?.data?.detail || 'Verification failed.';
      setVerifyMsg((m) => ({ ...m, website: { ok: false, text: detail } }));
    } finally {
      setBusyPlatform(null);
    }
  };

  const startX = async () => {
    // The full X OAuth handshake lives at /verify. Take the user there — they
    // finish the flow once, and the callback also populates socials.x.
    window.location.assign('/verify');
  };

  const comingSoon = (key) => {
    setVerifyMsg((m) => ({ ...m, [key]: { ok: false, text: 'OAuth verification is coming soon — link will appear as unverified for now.' } }));
  };

  return (
    <div className="socials-editor" data-testid="socials-editor">
      {PLATFORMS.map((p) => {
        const entry = local[p.key] || {};
        const url = entry.url || '';
        const verified = !!entry.verified;
        const followers = entry.followers;
        const msg = verifyMsg[p.key];
        return (
          <div className="socials-row" key={p.key} data-testid={`social-row-${p.key}`}>
            <div className="socials-row-head">
              <label htmlFor={`social-${p.key}`} className="socials-label">
                {p.label}
                {verified && (
                  <span className="socials-tick" title={`Verified via ${p.label}`} data-testid={`social-verified-${p.key}`}>
                    ✓ Verified
                  </span>
                )}
                {!verified && url && (
                  <span className="socials-untick" data-testid={`social-unverified-${p.key}`}>Unverified</span>
                )}
                {typeof followers === 'number' && followers > 0 && (
                  <span className="socials-followers" data-testid={`social-followers-${p.key}`}>
                    {compactNumber(followers)} followers
                  </span>
                )}
              </label>
            </div>
            <div className="socials-row-body">
              <input
                id={`social-${p.key}`}
                type="url"
                value={url}
                onChange={(e) => setUrl(p.key, e.target.value)}
                placeholder={p.placeholder}
                autoComplete="off"
                spellCheck={false}
                data-testid={`social-input-${p.key}`}
              />
              {p.verifiable === true && p.key === 'x' && !verified && (
                <button
                  type="button"
                  className="socials-verify-btn"
                  onClick={startX}
                  data-testid={`social-verify-${p.key}`}
                  disabled={busyPlatform === p.key}
                  title="Verifies via X OAuth (also grants the profile blue tick)"
                >Verify with X</button>
              )}
              {p.verifiable === true && p.key === 'website' && (
                <button
                  type="button"
                  className="socials-verify-btn"
                  onClick={verifyWebsite}
                  disabled={!url || busyPlatform === p.key}
                  data-testid={`social-verify-${p.key}`}
                >
                  {busyPlatform === p.key ? 'Checking…' : verified ? 'Re-verify' : 'Verify'}
                </button>
              )}
              {p.verifiable === 'soon' && (
                <button
                  type="button"
                  className="socials-verify-btn soon"
                  onClick={() => comingSoon(p.key)}
                  data-testid={`social-verify-${p.key}`}
                >Verify (soon)</button>
              )}
            </div>
            {p.key === 'website' && (
              <div className="socials-hint" data-testid="socials-website-hint">
                Add this to your site's <code>&lt;head&gt;</code>, then click Verify:<br />
                <code>{`<meta name="clanster-verify" content="${userId || 'YOUR_USER_ID'}">`}</code>
              </div>
            )}
            {msg && (
              <div className={`socials-msg ${msg.ok ? 'ok' : 'err'}`} data-testid={`social-msg-${p.key}`}>
                {msg.text}
              </div>
            )}
          </div>
        );
      })}
      <div className="socials-actions">
        <button
          type="button"
          className="socials-save-btn"
          disabled={!dirty || saving}
          onClick={save}
          data-testid="socials-save-btn"
        >
          {saving ? 'Saving…' : dirty ? 'Save links' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

function normalize(v) {
  const out = {};
  PLATFORMS.forEach((p) => {
    out[p.key] = v && v[p.key] ? { ...v[p.key] } : { url: '' };
  });
  return out;
}

function compactNumber(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}
